// Phase 22 — Admin Affiliate Management
// =======================================
// Real-data admin view of the 1200+ affiliate profile collection. Supports
// search (slug/code/email/name), status filter, tier filter, paginated
// loading, safe deactivation (suspend, not delete), and reactivate. All
// destructive actions require explicit confirmation. Profile rows are
// joined with their owning user record so admins see email + name inline.

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldOff, ShieldCheck, Loader2, RefreshCw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ADMIN_AFF } from "@/constants/testIds";
import SafeImage, { LATM_FALLBACK_LOGO } from "@/components/SafeImage";

function dollars(c) { return `$${((c || 0) / 100).toFixed(2)}`; }

const PAGE = 50;

export default function AdminAffiliateProfiles() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [tier, setTier] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const reload = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/affiliate/profiles", {
        params: { q: q || undefined, status: status || undefined, tier: tier || undefined, limit: PAGE, offset },
      });
      // Backward-compatible: accept array OR {profiles, total}
      if (Array.isArray(data)) {
        setRows(data);
        setTotal(data.length);
      } else {
        setRows(data.profiles || []);
        setTotal(data.total || 0);
      }
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [q, status, tier, offset]);

  useEffect(() => {
    const t = setTimeout(reload, 250); // debounce
    return () => clearTimeout(t);
  }, [reload]);

  const setProfileStatus = async (profile, nextStatus) => {
    // Safety confirmation for any destructive (deactivate) action.
    if (nextStatus === "suspended") {
      const confirmed = window.confirm(
        `Suspend affiliate "${profile.user_email || profile.slug}"?\n\n` +
        "This stops their commissions and hides their referral profile.\n" +
        "No data is deleted — you can reactivate at any time.",
      );
      if (!confirmed) return;
    }
    try {
      await api.put(`/admin/affiliate/profiles/${profile.id}/status`, { status: nextStatus });
      setOk(`Affiliate "${profile.slug}" → ${nextStatus}`);
      reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-affiliate-management">
      <header className="fade-up">
        <div className="text-xs uppercase tracking-[0.3em] text-lifted-green">Affiliate admin</div>
        <h1 className="font-anton text-3xl uppercase mt-1">Affiliate Management</h1>
        <p className="text-sm text-lifted-dim mt-1 max-w-2xl">
          Search, filter and safely deactivate affiliate profiles. Suspending does not delete — affiliates can be reactivated and their attribution history stays intact.
        </p>
      </header>

      {/* Filter bar */}
      <div className="glass-panel p-3 flex flex-wrap items-center gap-3" data-testid="affiliate-filters">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-lifted-dim" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setOffset(0); setQ(e.target.value); }}
            placeholder="Search by slug, referral code, email or name…"
            className="w-full bg-black/40 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-lifted-green/40"
            data-testid="affiliate-search"
          />
        </div>
        <select value={status} onChange={(e) => { setOffset(0); setStatus(e.target.value); }}
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                data-testid="affiliate-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
        <select value={tier} onChange={(e) => { setOffset(0); setTier(e.target.value); }}
                className="bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm"
                data-testid="affiliate-tier-filter">
          <option value="">All tiers</option>
          <option value="lift_off">Lift-Off</option>
          <option value="orbit">Orbit</option>
          <option value="galaxy">Galaxy</option>
          <option value="cosmos">Cosmos</option>
        </select>
        <button onClick={reload} className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="affiliate-refresh">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
        <div className="text-xs text-lifted-dim ml-auto" data-testid="affiliate-total">
          {loading ? "Loading…" : `${total.toLocaleString()} affiliate${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {err && <div className="text-rose-300 text-sm" data-testid="affiliate-err">{err}</div>}
      {ok && <div className="text-lifted-green text-sm" data-testid="affiliate-ok">{ok}</div>}

      {/* Iter68 — Card grid instead of table for human-readable visuals */}
      <div data-testid={ADMIN_AFF.profiles} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((p) => (
          <div
            key={p.id}
            data-testid={ADMIN_AFF.profileRow(p.id)}
            className="glass-panel p-3 flex items-center gap-3 border border-transparent hover:border-lifted-green/30 transition-colors"
          >
            <div className="relative w-12 h-12 rounded-full overflow-hidden bg-black/40 border border-white/10 shrink-0">
              <SafeImage
                src={p.avatar_url || LATM_FALLBACK_LOGO}
                alt={p.user_name || p.slug}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-lifted-text truncate" data-testid={`affiliate-row-${p.id}-name`}>
                {p.user_name || "—"}
              </div>
              <div className="text-xs text-lifted-dim truncate">{p.user_email || ""}</div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <Link
                  to={`/affiliate/u/${p.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-lifted-green hover:underline"
                  data-testid={`affiliate-row-${p.id}-slug`}
                >/{p.slug}</Link>
                <span className="chip chip--purple !text-[9px]">{p.tier}</span>
                {p.status === "active"
                  ? <span className="chip chip--green !text-[9px]">Active</span>
                  : <span className="chip !text-[9px]">Suspended</span>}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center">
                <div>
                  <div className="font-anton text-sm text-lifted-green leading-none">{p.stats?.referrals_count || 0}</div>
                  <div className="text-[8px] text-lifted-mute uppercase tracking-wider mt-0.5">Refs</div>
                </div>
                <div>
                  <div className="font-anton text-sm text-lifted-text leading-none">{dollars(p.stats?.gross_volume_cents)}</div>
                  <div className="text-[8px] text-lifted-mute uppercase tracking-wider mt-0.5">Volume</div>
                </div>
                <div>
                  <div className="font-anton text-sm text-lifted-green leading-none">{dollars(p.stats?.lifetime_commission_cents)}</div>
                  <div className="text-[8px] text-lifted-mute uppercase tracking-wider mt-0.5">Comm</div>
                </div>
              </div>
            </div>

            <div className="shrink-0">
              {p.status === "active" ? (
                <button
                  onClick={() => setProfileStatus(p, "suspended")}
                  className="btn-ghost !py-1.5 !px-2 !text-[0.6rem] inline-flex items-center gap-1 hover:text-rose-300"
                  data-testid={`affiliate-row-${p.id}-suspend`}>
                  <ShieldOff className="h-3 w-3" />
                </button>
              ) : (
                <button
                  onClick={() => setProfileStatus(p, "active")}
                  className="btn-ghost !py-1.5 !px-2 !text-[0.6rem] inline-flex items-center gap-1 hover:text-lifted-green"
                  data-testid={`affiliate-row-${p.id}-reactivate`}>
                  <ShieldCheck className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className="col-span-full text-center text-lifted-mute py-10" data-testid="affiliate-empty">
            No affiliate profiles match this filter.
          </div>
        )}
        {loading && (
          <div className="col-span-full text-center text-lifted-dim py-10">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > PAGE && (
        <div className="flex items-center justify-between gap-3" data-testid="affiliate-pagination">
          <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
                  className="btn-ghost text-xs disabled:opacity-40" data-testid="affiliate-prev-page">
            ← Previous
          </button>
          <span className="text-xs text-lifted-dim">
            {offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}
          </span>
          <button onClick={() => setOffset(offset + PAGE)} disabled={offset + PAGE >= total}
                  className="btn-ghost text-xs disabled:opacity-40" data-testid="affiliate-next-page">
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
